import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useConfig } from "@/context/ConfigContext";
import { Eyebrow } from "@/components/common";

const KEY = "mr-invitation-draft";
const EMPTY = { name: "", phone: "", email: "", city: "", company: "", sector: "", seniority: "", company_size: "", importing: "", brings: "", seeks: "", sponsor_name: "", heard_from: "", consent: false };
const SENIORITY = ["Moins de 2 ans", "2 à 5 ans", "5 à 10 ans", "Plus de 10 ans"];
const SIZES = ["Seul(e)", "2 à 5 personnes", "6 à 20 personnes", "Plus de 20 personnes"];
const HEARD = ["Un membre du club", "Bouche-à-oreille", "Réseaux sociaux", "Événement", "Autre"];
const STEPS = ["Vous", "Votre activité", "Vos besoins", "Envoi"];

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mr-label">{label}</span>
    {children}
  </label>
);

export default function InvitationRequest() {
  const { settings } = useConfig();
  const [form, setForm] = useState(() => ({ ...EMPTY, ...JSON.parse(localStorage.getItem(KEY) || "{}") }));
  const [step, setStep] = useState(0);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(form));
  }, [form]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const sectors = settings["catalog.sectors"] || [];
  const cities = settings["catalog.cities"] || [];

  const next = () => {
    setError("");
    if (step === 0 && (form.name.trim().length < 2 || form.phone.replace(/\D/g, "").length < 8)) return setError("Indiquez votre nom et un numéro de téléphone valide.");
    if (step === 1 && (!form.company.trim() || !form.sector || !form.seniority)) return setError("Indiquez votre entreprise, votre secteur et votre ancienneté.");
    if (step === 2 && (!form.brings.trim() || !form.seeks.trim())) return setError("Dites-nous ce que vous apportez et ce que vous cherchez.");
    setStep(step + 1);
  };

  const submit = async () => {
    setError("");
    if (!form.consent) return setError("Merci d'accepter le traitement de vos données.");
    setBusy(true);
    try {
      const { data } = await api.post("/public/invitation-requests", { ...form, email: form.email || null });
      localStorage.removeItem(KEY);
      setDone(data.message);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (done)
    return (
      <section className="max-w-xl mx-auto px-5 py-24 text-center mr-fade-up" data-testid="invitation-success">
        <CheckCircle2 className="mx-auto text-mr-accent mb-6" size={40} />
        <h1 className="mr-h2">Demande envoyée</h1>
        <p className="text-mr-text2 mt-4">{done}</p>
      </section>
    );

  return (
    <section className="max-w-2xl mx-auto px-5 py-16 lg:py-24 mr-fade-up">
      <Eyebrow className="mb-4">Demande d'invitation</Eyebrow>
      <h1 className="mr-h1">Rejoindre <span className="mr-accent-word">le cercle</span></h1>
      <p className="text-mr-text2 mt-4 mb-10">{settings["texts.invitation_intro"]}</p>
      <div className="flex gap-2 mb-8" aria-label="Étapes">
        {STEPS.map((l, i) => (
          <div key={l} className="flex-1">
            <div className={`h-0.5 ${i <= step ? "bg-mr-accent" : "bg-mr-border"} transition-colors`} />
            <p className={`text-xs mt-2 ${i === step ? "text-mr-text" : "text-mr-muted"}`}>{i + 1}. {l}</p>
          </div>
        ))}
      </div>
      <div className="mr-card p-6 sm:p-8 space-y-5">
        {step === 0 && (
          <>
            <Field label="Nom et prénom"><input data-testid="invreq-name-input" className="mr-input" value={form.name} onChange={set("name")} placeholder="Ex. Aminata Koné" /></Field>
            <Field label="Téléphone (WhatsApp)"><input data-testid="invreq-phone-input" className="mr-input tabular" inputMode="tel" value={form.phone} onChange={set("phone")} placeholder="07 00 00 00 00" /></Field>
            <Field label="E-mail"><input data-testid="invreq-email-input" className="mr-input" type="email" value={form.email} onChange={set("email")} placeholder="vous@exemple.ci" /></Field>
            <Field label="Ville / commune">
              <select data-testid="invreq-city-select" className="mr-input" value={form.city} onChange={set("city")}>
                <option value="">Choisir…</option>
                {cities.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
          </>
        )}
        {step === 1 && (
          <>
            <Field label="Entreprise"><input data-testid="invreq-company-input" className="mr-input" value={form.company} onChange={set("company")} /></Field>
            <Field label="Secteur">
              <select data-testid="invreq-sector-select" className="mr-input" value={form.sector} onChange={set("sector")}>
                <option value="">Choisir…</option>
                {sectors.map((s) => <option key={s}>{s}</option>)}
              </select>
            </Field>
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Ancienneté de l'activité"><select data-testid="invreq-seniority-select" className="mr-input" value={form.seniority} onChange={set("seniority")}><option value="">Choisir…</option>{SENIORITY.map((s) => <option key={s}>{s}</option>)}</select></Field>
              <Field label="Taille de l'entreprise (facultatif)"><select data-testid="invreq-size-select" className="mr-input" value={form.company_size} onChange={set("company_size")}><option value="">Choisir…</option>{SIZES.map((s) => <option key={s}>{s}</option>)}</select></Field>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <Field label="Importation actuelle ou envisagée"><input data-testid="invreq-importing-input" className="mr-input" value={form.importing} onChange={set("importing")} placeholder="Ex. textile depuis la Chine, 2 conteneurs par an" /></Field>
            <Field label="Ce que vous apportez"><textarea data-testid="invreq-brings-input" className="mr-input min-h-[90px]" value={form.brings} onChange={set("brings")} /></Field>
            <Field label="Ce que vous cherchez"><textarea data-testid="invreq-seeks-input" className="mr-input min-h-[90px]" value={form.seeks} onChange={set("seeks")} /></Field>
          </>
        )}
        {step === 3 && (
          <>
            <Field label="Membre qui vous recommande (facultatif)"><input data-testid="invreq-sponsor-input" className="mr-input" value={form.sponsor_name} onChange={set("sponsor_name")} /></Field>
            <Field label="Comment avez-vous connu Monrezo ?"><select data-testid="invreq-heard-select" className="mr-input" value={form.heard_from} onChange={set("heard_from")}><option value="">Choisir…</option>{HEARD.map((s) => <option key={s}>{s}</option>)}</select></Field>
            <label className="flex items-start gap-3 text-sm text-mr-text2 cursor-pointer">
              <input data-testid="invreq-consent-checkbox" type="checkbox" className="mt-1 w-5 h-5 accent-[var(--mr-accent)]" checked={form.consent} onChange={set("consent")} />
              J'accepte que Monrezo traite ces informations pour étudier ma demande, conformément à la loi ivoirienne sur les données personnelles.
            </label>
          </>
        )}
        {error && <p data-testid="invreq-error" className="text-sm text-mr-danger">{error}</p>}
        <div className="flex justify-between pt-2">
          {step > 0 ? (
            <button data-testid="invreq-back-button" className="mr-btn-ghost" onClick={() => setStep(step - 1)}><ArrowLeft size={16} /> Retour</button>
          ) : <span />}
          {step < 3 ? (
            <button data-testid="invreq-next-button" className="mr-btn-primary" onClick={next}>Continuer <ArrowRight size={16} /></button>
          ) : (
            <button data-testid="invreq-submit-button" className="mr-btn-primary" disabled={busy} onClick={submit}>{busy ? "Envoi…" : "Envoyer ma demande"} <ArrowRight size={16} /></button>
          )}
        </div>
        <p className="text-xs text-mr-muted">Brouillon enregistré automatiquement sur cet appareil.</p>
      </div>
    </section>
  );
}
